import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { FileSpreadsheet, Loader2, Download, CheckCircle2, AlertTriangle, Upload } from "lucide-react";

const SAMPLE = "name,email,business\nJane Doe,jane@example.com,Jane Doe LLC\nSam Lee,sam@example.com,Lee Consulting LLC\n";

export const BulkImportPanel = ({ onDone }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadSample = () => {
    const url = URL.createObjectURL(new Blob([SAMPLE], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "scorppath-clients-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const importCsv = async () => {
    if (!file) { toast.error("Choose a CSV file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/staff/clients/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.created_count} client${data.created_count === 1 ? "" : "s"} onboarded${data.skipped_count ? `, ${data.skipped_count} skipped` : ""}`);
      if (data.created_count > 0) onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  if (result) {
    return (
      <div className="space-y-4 mt-2" data-testid="bulk-result">
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[#E6F0EB] rounded-xl p-3">
            <p className="font-heading text-2xl font-bold text-[#114A31]" data-testid="bulk-created-count">{result.created_count}</p>
            <p className="text-xs text-[#114A31]">Created · invites {result.invites_sending ? "sending in background" : "not emailed (copy links from pipeline)"}</p>
          </div>
          <div className="bg-[#FEF3C7] rounded-xl p-3">
            <p className="font-heading text-2xl font-bold text-[#92600A]" data-testid="bulk-skipped-count">{result.skipped_count}</p>
            <p className="text-xs text-[#92600A]">Skipped</p>
          </div>
        </div>
        {result.created.length > 0 && (
          <ul className="max-h-40 overflow-auto space-y-1 text-sm" data-testid="bulk-created-list">
            {result.created.map((c) => (
              <li key={c.user_id} className="flex items-center gap-2 bg-[#F9F9F8] rounded-lg px-3 py-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-[#166534] shrink-0" />
                <span className="truncate text-[#1C1C1C]">{c.name}</span>
                <span className="truncate text-[#595D5A] text-xs">{c.email}</span>
              </li>
            ))}
          </ul>
        )}
        {result.skipped.length > 0 && (
          <ul className="max-h-32 overflow-auto space-y-1 text-sm" data-testid="bulk-skipped-list">
            {result.skipped.map((s, i) => (
              <li key={i} className="flex items-center gap-2 bg-[#FFF7ED] rounded-lg px-3 py-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-[#92600A] shrink-0" />
                <span className="text-xs text-[#595D5A]">Row {s.row}</span>
                <span className="truncate text-[#1C1C1C] text-xs">{s.email || "—"}</span>
                <span className="ml-auto text-xs text-[#92600A] shrink-0">{s.reason}</span>
              </li>
            ))}
          </ul>
        )}
        <Button onClick={() => { setResult(null); setFile(null); }} variant="outline" data-testid="bulk-import-another"
          className="w-full rounded-full border-[#E5E5E5]">Import another file</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-2" data-testid="bulk-import-panel">
      <div className="bg-[#F9F9F8] border border-[#E5E5E5] rounded-xl p-3 text-xs text-[#595D5A] leading-relaxed">
        Columns: <code className="text-[#114A31]">name</code>, <code className="text-[#114A31]">email</code> (required), <code className="text-[#114A31]">business</code>.
        Each row creates a portal account + "Onboarding" case and emails a set-password invite. Existing emails are skipped. Max 500 rows.
        <button onClick={downloadSample} data-testid="bulk-sample-btn" className="ml-1 inline-flex items-center gap-1 text-[#114A31] font-semibold hover:underline">
          <Download className="h-3 w-3" /> Template
        </button>
      </div>
      <div>
        <Label>CSV file</Label>
        <input ref={inputRef} type="file" accept=".csv,text/csv" data-testid="bulk-file-input" className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <Button onClick={() => inputRef.current?.click()} variant="outline" data-testid="bulk-choose-btn"
          className="mt-1.5 w-full rounded-full border-dashed border-[#114A31] text-[#114A31] hover:bg-[#E6F0EB] justify-start">
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          <span className="truncate" data-testid="bulk-file-name">{file ? file.name : "Choose a CSV file"}</span>
        </Button>
      </div>
      <Button onClick={importCsv} disabled={busy || !file} data-testid="bulk-import-btn"
        className="w-full rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
        Import & send invites
      </Button>
    </div>
  );
};

export default BulkImportPanel;
